import { ChevronDown, Printer, ReceiptText, Scissors, Tag, Truck } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@bookdental/ui";
import type { ProsthesisServiceRow } from "@/components/prosthesis/types";
import type { IncomingOrderRow } from "./production-types";
import { useImpressaoDoPedido } from "./use-impressao-do-pedido";

/**
 * "Imprimir ▾" no cabeçalho dos dialogs do pedido e do serviço: etiquetas de produção (todas, ou só
 * a do serviço aberto), etiqueta de entrega e recibo. O que não se aplica fica desabilitado com o
 * motivo — sumir sem explicar faz a pessoa procurar o botão.
 */
export function MenuDeImpressao({ order, servico }: { order: IncomingOrderRow; servico?: ProsthesisServiceRow }) {
  const { producao, etiquetaDeEntrega, etiquetaUnica, recibo, entrega, temValor, podeVerPreco, quantidade } = useImpressaoDoPedido(order, servico);
  const motivo = "text-[11px] font-normal text-zinc-400 dark:text-muted-foreground";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-2.5 text-[12px] font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-muted"
        >
          <Printer className="h-3.5 w-3.5" />
          Imprimir
          <ChevronDown className="h-3.5 w-3.5 text-zinc-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 border border-zinc-200 bg-white shadow-lg dark:border-border dark:bg-card">
        <DropdownMenuItem onSelect={etiquetaUnica} className="items-start gap-2">
          <Scissors className="mt-0.5 h-4 w-4 text-zinc-500" />
          <span className="flex flex-col">
            {servico ? "Etiqueta única" : `Etiquetas únicas (${quantidade})`}
            <span className={motivo}>Produção e expedição, com linha de corte</span>
          </span>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={producao} className="gap-2">
          <Tag className="h-4 w-4 text-zinc-500" />
          {servico ? "Etiqueta de produção" : `Etiquetas de produção (${quantidade})`}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={etiquetaDeEntrega} disabled={!entrega} className="items-start gap-2">
          <Truck className="mt-0.5 h-4 w-4 text-zinc-500" />
          <span className="flex flex-col">
            Etiqueta de entrega
            {!entrega && <span className={motivo}>Pedido sem endereço de envio</span>}
          </span>
        </DropdownMenuItem>
        {podeVerPreco && (
          <DropdownMenuItem onSelect={recibo} disabled={!temValor} className="items-start gap-2">
            <ReceiptText className="mt-0.5 h-4 w-4 text-zinc-500" />
            <span className="flex flex-col">
              Recibo
              {!temValor && <span className={motivo}>Nenhum serviço com valor</span>}
            </span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
